import styled, { css, keyframes } from "styled-components";
import { BRAND_C as C, COLORS } from "../../common/colors";

// success / error semantic colours (not in BRAND_C — kept as local constants)
const success   = COLORS.primary;
const successBg = COLORS.bgCool;
const error     = COLORS.error;
const errorBg   = COLORS.bgWarmLight;

// ─── Keyframes ────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to   { opacity: 1; transform: scale(1);    }
`;

export const kSlideDown = keyframes`
  from { opacity: 0; max-height: 0;   transform: translateY(-8px); }
  to   { opacity: 1; max-height: 200px; transform: translateY(0);  }
`;

export const kSpinRing = keyframes`
  to { transform: rotate(360deg); }
`;

export const kCheckPop = keyframes`
  0%   { transform: scale(0) rotate(-12deg); opacity: 0; }
  60%  { transform: scale(1.15) rotate(3deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE SHELL
// ═══════════════════════════════════════════════════════════════════════════════

export const Section = styled.section`
  position: relative;
  overflow: hidden;
  background: ${COLORS.pageBg};
  min-height: 100vh;
  padding-bottom: 88px;
`;

export const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

// ─── Background decoration (matches Packages) ─────────────────────────────────

export const BgBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;

  &.b1 { width: 560px; height: 560px; background: ${COLORS.primaryTint}; top: -140px; left: -160px; }
  &.b2 { width: 400px; height: 400px; background: rgba(245,197,24,0.07); top: 40%; right: -120px; }
  &.b3 { width: 300px; height: 300px; background: ${COLORS.primaryTint}; bottom: 100px; left: 32%; }
`;
BgBlob.displayName = "BgBlob";

export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: radial-gradient(circle, rgba(61,107,94,0.07) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(to bottom, transparent, rgba(0,0,0,0.45) 18%, rgba(0,0,0,0.45) 82%, transparent);
`;
DotGrid.displayName = "DotGrid";

// ═══════════════════════════════════════════════════════════════════════════════
// HERO — matches Packages HeroContent pattern
// ═══════════════════════════════════════════════════════════════════════════════

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 80px 24px 52px;
  max-width: 700px;
  margin: 0 auto;
  animation: ${kFadeUp} 0.65s ease both;

  @media (max-width: 600px) { padding: 56px 20px 36px; }
`;

export const HeroEyebrow = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${C.brand};
  margin-bottom: 14px;
`;

export const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 800;
  color: ${C.text};
  line-height: 1.14;
  margin-bottom: 18px;
  letter-spacing: -0.02em;
`;

export const HeroDivider = styled.div`
  width: 52px; height: 3px;
  background: linear-gradient(90deg, ${C.brand}, ${C.accent});
  border-radius: 2px;
  margin: 0 auto 20px;
`;

export const HeroDesc = styled.p`
  font-size: clamp(0.92rem, 2vw, 1.05rem);
  color: ${C.textMid};
  line-height: 1.78;
  max-width: 540px;
  margin: 0 auto;
`;

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN FORM LAYOUT — mirrors Packages PackageLayout (side panel + content)
// ═══════════════════════════════════════════════════════════════════════════════

export const FormBlock = styled.div`
  position: relative;
  z-index: 1;
  margin-bottom: 56px;
  animation: ${kFadeUp} 0.55s ease both;
  animation-delay: ${({ $delay }) => $delay ?? 0}s;
`;

export const FormLayout = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 32px rgba(61,107,94,0.10), 0 1px 4px rgba(0,0,0,0.04);

  @media (max-width: 700px) {
    flex-direction: column;
    border-radius: 20px;
  }
`;

// ─── Left image panel (same as SidePanel in Packages) ────────────────────────

export const SidePanel = styled.div`
  position: relative;
  width: 260px;
  flex-shrink: 0;
  min-height: 400px;
  overflow: hidden;

  @media (min-width: 1000px) { width: 300px; }

  @media (max-width: 700px) {
    width: 100%;
    min-height: 180px;
    max-height: 220px;
  }
`;

export const SidePanelImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 20%;
  display: block;
  transition: transform 0.5s ease;

  ${SidePanel}:hover & { transform: scale(1.04); }
`;

export const SidePanelOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10,4,32,0.90) 0%,
    rgba(10,4,32,0.48) 30%,
    rgba(10,4,32,0) 58%
  );

  @media (max-width: 700px) {
    background: linear-gradient(
      to top,
      rgba(10,4,32,0.82) 0%,
      rgba(10,4,32,0.36) 35%,
      rgba(10,4,32,0) 65%
    );
  }
`;

export const SidePanelMeta = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  padding: 20px 24px 28px;

  @media (max-width: 700px) { padding: 14px 18px 20px; }
`;

export const PanelEyebrow = styled.p`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.62);
  margin-bottom: 6px;
`;

export const PanelTitle = styled.h3`
  font-size: clamp(1.1rem, 2.2vw, 1.5rem);
  font-weight: 800;
  color: #ffffff;
  line-height: 1.22;
  margin-bottom: 10px;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 10px rgba(0,0,0,0.35);
`;

export const PanelAccent = styled.div`
  width: 28px; height: 2px;
  background: linear-gradient(90deg, ${C.accent}, ${C.brandLight});
  border-radius: 2px;
  margin-bottom: 10px;
`;

export const PanelContact = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255,255,255,0.78);
  text-decoration: none;
  transition: color 0.2s;

  &:hover { color: ${C.accent}; }

  svg { width: 14px; height: 14px; flex-shrink: 0; }
`;

// ─── Right form content area ──────────────────────────────────────────────────

export const FormContent = styled.div`
  flex: 1;
  background: ${C.surfaceAlt};
  border-left: 1.5px solid ${C.border};
  padding: 36px 32px 36px;
  display: flex;
  flex-direction: column;

  @media (max-width: 700px) {
    border-left: none;
    border-top: 1.5px solid ${C.border};
    padding: 24px 20px 28px;
  }
`;

export const FormTitle = styled.h2`
  font-size: clamp(1.3rem, 2.5vw, 1.7rem);
  font-weight: 800;
  color: ${C.text};
  margin-bottom: 6px;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

export const FormSubtitle = styled.p`
  font-size: 0.88rem;
  color: ${C.textSoft};
  margin-bottom: 24px;
  line-height: 1.5;
`;

// ─── Toggle switch (Prenatal / Postnatal) ─────────────────────────────────────

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  background: ${C.muted};
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 24px;
  gap: 0;
  width: 100%;
`;

export const ToggleBtn = styled.button`
  flex: 1;
  padding: 9px 14px;
  border-radius: 9px;
  border: none;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  min-height: 40px;

  background: ${({ $active }) => $active ? C.brand      : "transparent"};
  color:      ${({ $active }) => $active ? "#ffffff"    : C.textMid};
  box-shadow: ${({ $active }) => $active ? "0 2px 10px rgba(61,107,94,0.28)" : "none"};

  &:focus-visible { outline: 2px solid ${C.brand}; outline-offset: 2px; }
`;

// ─── Form fields ──────────────────────────────────────────────────────────────

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols === 2 ? "1fr 1fr" : "1fr"};
  gap: 14px 12px;
  margin-bottom: 14px;

  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const FieldLabel = styled.label`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${C.textMid};
`;

const inputBase = css`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1.5px solid ${({ $error }) => $error ? error : C.border};
  background: ${C.surface};
  font-size: 0.9rem;
  color: ${C.text};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  -webkit-appearance: none;
  min-height: 46px;

  &::placeholder { color: ${C.textSoft}; opacity: 0.7; }

  &:focus {
    outline: none;
    border-color: ${C.brand};
    box-shadow: 0 0 0 3px ${COLORS.primaryTint};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const InputField = styled.input`${inputBase}`;
InputField.displayName = "InputField";

export const SelectField = styled.select`
  ${inputBase}
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237e789c' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  appearance: none;
`;
SelectField.displayName = "SelectField";

export const TextAreaField = styled.textarea`
  ${inputBase}
  min-height: 88px;
  resize: vertical;
  line-height: 1.55;
`;
TextAreaField.displayName = "TextAreaField";

export const FieldError = styled.span`
  font-size: 0.72rem;
  color: ${error};
  font-weight: 600;
  animation: ${kSlideDown} 0.2s ease both;
`;
FieldError.displayName = "FieldError";

export const FieldHint = styled.span`
  font-size: 0.7rem;
  color: ${C.textSoft};
`;
FieldHint.displayName = "FieldHint";

// ─── Submit button ────────────────────────────────────────────────────────────

export const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
  margin-top: 8px;
  min-height: 52px;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s, opacity 0.2s;

  background: ${({ disabled }) =>
    disabled ? "#c4bfdf" : `linear-gradient(135deg, ${C.brandDark}, ${C.brand})`};
  color: #ffffff;
  box-shadow: ${({ disabled }) =>
    disabled ? "none" : "0 4px 18px rgba(61,107,94,0.32)"};

  svg { width: 16px; height: 16px; transition: transform 0.2s; flex-shrink: 0; }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(61,107,94,0.40);
    svg { transform: translateX(3px); }
  }
  &:active:not(:disabled) { transform: translateY(0) scale(0.98); }
  &:focus-visible { outline: 3px solid ${C.accent}; outline-offset: 2px; }
`;
SubmitBtn.displayName = "SubmitBtn";

// ─── Spinner ──────────────────────────────────────────────────────────────────

export const SpinnerRing = styled.span`
  display: inline-block;
  width: 18px; height: 18px;
  border: 2.5px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${kSpinRing} 0.7s linear infinite;
  flex-shrink: 0;
`;
SpinnerRing.displayName = "SpinnerRing";

// ─── Success modal content ────────────────────────────────────────────────────

export const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 28px;
  text-align: center;
`;

export const SuccessIcon = styled.div`
  width: 72px; height: 72px;
  border-radius: 50%;
  background: ${successBg};
  border: 2px solid rgba(22,163,74,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  animation: ${kCheckPop} 0.5s cubic-bezier(0.34,1.56,0.64,1) both;

  svg { width: 34px; height: 34px; color: ${success}; }
`;

export const SuccessTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 800;
  color: ${C.text};
  margin-bottom: 8px;
  letter-spacing: -0.01em;
`;

export const SuccessDesc = styled.p`
  font-size: 0.9rem;
  color: ${C.textMid};
  line-height: 1.65;
  margin: 0;
`;

// ─── Phone / contact link ─────────────────────────────────────────────────────

export const ContactLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  font-size: 0.85rem;
  color: ${C.textSoft};

  a {
    font-weight: 700;
    color: ${C.brand};
    text-decoration: none;
    transition: color 0.2s;
    &:hover { color: ${C.brandDark}; }
  }

  svg { width: 16px; height: 16px; color: ${C.brand}; flex-shrink: 0; }
`;
ContactLine.displayName = "ContactLine";

// ─── Dynamic fields enter animation ──────────────────────────────────────────

export const DynamicFields = styled.div`
  animation: ${kFadeUp} 0.3s ease both;
`;
DynamicFields.displayName = "DynamicFields";